import {view, Badge, Button} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {Card} from '@stripe-internal/conversations-ui';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

export type CaseStatus = 'open' | 'closed';

export type ValueProps = {
  has_related_case: boolean;
  sco_id?: string;
  created?: number;
  status?: CaseStatus;
  subject?: string;
};

const messages = defineMessages({
  createdLabel: {
    id: 'mse_inline_data.merchant_related_case.created_label',
    description: 'Label for displaying the creation date of a related case.',
    defaultMessage: 'Created',
  },
  caseIDLabel: {
    id: 'mse_inline_data.merchant_related_case.caseID_label',
    description: 'Label for displaying the case ID of a related case.',
    defaultMessage: 'Case ID',
  },
  statusLabel: {
    id: 'mse_inline_data.merchant_related_case.status_label',
    description: 'Label for displaying the status of a related case.',
    defaultMessage: 'Status',
  },
  viewInSupportCenterCTA: {
    id: 'mse_inline_data.merchant_related_case.view_in_support_center_cta',
    description:
      'Call to action for button linking to case in dashboard support center.',
    defaultMessage: 'View in Support Center',
  },
  openStatus: {
    id: 'mse_inline_data.merchant_related_case.open_status',
    description: 'Status for an open case.',
    defaultMessage: 'Open',
  },
  closedStatus: {
    id: 'mse_inline_data.merchant_related_case.closed_status',
    description: 'Status for an closed case.',
    defaultMessage: 'Closed',
  },
  openStatusDescription: {
    id: 'mse_inline_data.merchant_related_case.open_status_description',
    description: 'Status description for an open case.',
    defaultMessage:
      'This case is being looked into by a Support specialist who will respond via email within the next 48 hours.',
  },
});

export const MerchantRelatedCase = (props: InlineDatumReactComponentProps) => {
  const {datum} = props;
  const value = datum.value as ValueProps;
  const intl = useIntl();

  const {
    has_related_case: hasRelatedCase,
    sco_id: caseID,
    created,
    status,
    subject,
  } = value;

  const localizedCreatedDate =
    created &&
    intl.formatDate(new Date(created * 1000), {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });

  const analytics = useAnalytics();

  analytics.viewed('merchant_related_case', {
    hasRelatedCase,
  });

  if (!hasRelatedCase) {
    return null;
  }

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <Card>
        <view.div css={{stack: 'y', gap: 'medium'}}>
          <view.div data-testid="case-subject" css={{font: 'heading.small'}}>
            {subject}
          </view.div>
          {caseID ? (
            <view.div css={{stack: 'y'}}>
              <view.div css={{font: 'label.medium.emphasized'}}>
                <FormattedMessage {...messages.caseIDLabel} />
              </view.div>
              <view.div data-testid="case-id" css={{font: 'label.medium'}}>
                {caseID}
              </view.div>
            </view.div>
          ) : null}
          {created ? (
            <view.div css={{stack: 'y'}}>
              <view.div css={{font: 'label.medium.emphasized'}}>
                <FormattedMessage {...messages.createdLabel} />
              </view.div>
              <view.div data-testid="created-date" css={{font: 'label.medium'}}>
                {localizedCreatedDate}
              </view.div>
            </view.div>
          ) : null}
          {status ? (
            <view.div css={{stack: 'y', gap: 'small'}}>
              <view.div>
                <view.div
                  data-testid="status-label"
                  css={{font: 'label.medium.emphasized'}}
                >
                  <FormattedMessage {...messages.statusLabel} />
                </view.div>
                <Badge data-testid="status-badge" type="info">
                  {status === 'open' ? (
                    <FormattedMessage {...messages.openStatus} />
                  ) : (
                    <FormattedMessage {...messages.closedStatus} />
                  )}
                </Badge>
              </view.div>
              {status === 'open' ? (
                <view.div
                  data-testid="status-description"
                  css={{font: 'label.small'}}
                >
                  <FormattedMessage {...messages.openStatusDescription} />
                </view.div>
              ) : null}
            </view.div>
          ) : null}
          {caseID ? (
            <Button
              data-testid="cta-button"
              type="secondary"
              css={{
                width: 'fill',
                fontSize: 'label.medium.emphasized',
              }}
              onPress={() => {
                const ctaLink = `https://dashboard.stripe.com/support/${caseID}`;
                safeWindowOpen(ctaLink);
                analytics.action('uh_merchant_related_case_cta_button', {
                  ctaLink,
                });
              }}
            >
              <FormattedMessage {...messages.viewInSupportCenterCTA} />
            </Button>
          ) : null}
        </view.div>
      </Card>
    </view.div>
  );
};
